// Pre-insert duplicate check for daily reports.
// Checks by identity (date + label/herd/flock) and blocks inserts when
// a matching report already exists. Add Feed rows (source='add_feed_webform')
// are excluded for tables that share rows with Add Feed — egg_dailys never
// has Add Feed rows so the source filter is skipped there.

import type {SupabaseClient} from './supabaseTypes.js';

// The identity columns a daily report is deduplicated on, per table. Keyed by
// plain `string` rather than a union of the six table names because
// `checkDailyDuplicate` is reached with a caller-supplied table and already
// treats an unlisted one as "no identity, nothing to check" — a union would
// make that documented fallback unreachable instead of describing it.
const IDENTITY_FIELDS: Record<string, readonly string[] | undefined> = {
  poultry_dailys: ['date', 'batch_label'],
  pig_dailys: ['date', 'batch_label'],
  layer_dailys: ['date', 'batch_label'],
  egg_dailys: ['date'],
  cattle_dailys: ['date', 'herd'],
  sheep_dailys: ['date', 'flock'],
};

// The record page each daily table's report opens on.
const RECORD_PATH_PREFIX: Record<string, string | undefined> = {
  poultry_dailys: '/broiler/dailys/',
  layer_dailys: '/layer/dailys/',
  egg_dailys: '/layer/eggs/',
  pig_dailys: '/pig/dailys/',
  cattle_dailys: '/cattle/dailys/',
  sheep_dailys: '/sheep/dailys/',
};

const TABLES_WITH_ADD_FEED = new Set(['poultry_dailys', 'pig_dailys', 'layer_dailys', 'cattle_dailys', 'sheep_dailys']);

// A daily report as this module reads it: the identity columns it matches on,
// plus whatever else the submitting surface put on the row. Values stay
// `unknown` because the columns are typed by the database, not by this client,
// and every read below already coerces.
export type DailyRecord = {
  date?: unknown;
  batch_label?: unknown;
  herd?: unknown;
  flock?: unknown;
  [column: string]: unknown;
};

// `typeof x === 'object'` alone leaves `unknown` as `object`, which has no
// readable properties. This is the narrowing every error branch below needs,
// written once as a predicate so no call site casts.
function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// The existing report a duplicate check found. `owner_profile_id` and
// `submitted_at` are what canEditOwnRecord needs to decide whether the caller
// may correct that report in place instead of being blocked by it.
export type ExistingDailyReport = {
  id: string;
  date?: string | null;
  owner_profile_id?: string | null;
  submitted_at?: string | null;
};

export async function checkDailyDuplicate(
  sb: SupabaseClient,
  table: string,
  record: DailyRecord,
): Promise<ExistingDailyReport | null> {
  const fields = IDENTITY_FIELDS[table];
  if (!fields) return null;
  for (const f of fields) {
    if (!record[f] && record[f] !== 0) return null;
  }
  let query = sb.from(table).select('id,date,owner_profile_id,submitted_at').is('deleted_at', null).limit(1);
  if (TABLES_WITH_ADD_FEED.has(table)) {
    query = query.or('source.is.null,source.neq.add_feed_webform');
  }
  for (const f of fields) {
    query = query.eq(f, record[f]);
  }
  const {data, error} = await query;
  if (error) {
    throw new Error(`Could not verify duplicate report: ${error.message}`);
  }
  if (!data || data.length === 0) return null;
  return data[0] as ExistingDailyReport;
}

export function dailyReportPath(table: string, id: string): string | null {
  const prefix = RECORD_PATH_PREFIX[table];
  return prefix && id ? prefix + id : null;
}

// Columns a new submission carries that an edit of an existing report must
// not send. update_daily_report ignores anything off its allowlist anyway;
// dropping them here keeps the patch honest about what it changes.
// `team_member` stays the existing report's, as on the report pages, where it
// is a locked field.
const SUBMISSION_ONLY_COLUMNS = new Set([
  'team_member',
  'id',
  'client_submission_id',
  'submitted_at',
  'photos',
  'source',
  'daily_submission_id',
  'owner_profile_id',
  'deleted_at',
  'deleted_by',
]);

// The edit patch that applies a blocked submission's values to the existing
// report it collided with.
export function duplicateUpdatePatch(record: DailyRecord): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(record)) {
    if (!SUBMISSION_ONLY_COLUMNS.has(k)) patch[k] = v;
  }
  return patch;
}

export function identityKey(table: string, record: DailyRecord): string | null {
  const fields = IDENTITY_FIELDS[table];
  if (!fields) return null;
  return fields.map((f) => String(record[f] || '')).join('|');
}

export function checkInSubmissionDuplicates(table: string, records: DailyRecord[]): DailyRecord | null {
  const seen = new Set<string>();
  for (const rec of records) {
    const key = identityKey(table, rec);
    if (!key) continue;
    if (seen.has(key)) return rec;
    seen.add(key);
  }
  return null;
}

export function formatDuplicateError(table: string, record: DailyRecord): string {
  const label = String(record.batch_label || record.herd || record.flock || '');
  const date = String(record.date || '');
  if (label) {
    return `A report already exists for ${label} on ${date}. Edit the existing report if changes are needed.`;
  }
  return `A report already exists for ${date}. Edit the existing report if changes are needed.`;
}

// True when a Supabase error is a violation of the active-daily-identity
// UNIQUE indexes added in migration 084 (date + batch_label/herd/flock).
// Accepts either a Supabase error object ({code, message}) or a bare message
// string (runMutation's onError passes only error.message). The constraint
// name is the reliable signal — Postgres includes it in the 23505 message.
export function isDailyIdentityViolation(errOrMsg: unknown): boolean {
  if (!errOrMsg) return false;
  const msg = typeof errOrMsg === 'string' ? errOrMsg : String((isObjectLike(errOrMsg) && errOrMsg.message) || '');
  const code = isObjectLike(errOrMsg) ? String(errOrMsg.code || '') : '';
  return /_active_daily_identity_uq/i.test(msg) || (code === '23505' && /_active_daily_identity_uq/i.test(msg));
}

// Maps a Supabase write error to a user-facing message: the friendly
// "report already exists" copy when it's an identity-index collision, else the
// raw error message unchanged. `record` is best-effort (date + identity) for
// the friendly copy; missing fields fall back to the date-only phrasing.
export function friendlyDailyDbError(errOrMsg: unknown, table: string, record: DailyRecord = {}): string {
  if (isDailyIdentityViolation(errOrMsg)) {
    return formatDuplicateError(table, record);
  }
  return typeof errOrMsg === 'string' ? errOrMsg : String((isObjectLike(errOrMsg) && errOrMsg.message) || errOrMsg);
}
